import type { PriceSettingsDto, PriceUnitDto } from '@/api';
import { formatDecimal } from '@/components/table/table-format-helpers';
import type { PricelistSettings } from '@/pages/price-planning/simulation/simulation-tool-page/utils/simulation-types';
import { normalizeNumericString } from '@/utils/number';

export const GAP = 8;
export const VALUE_COL_WIDTH = 172;
export const LABEL_COL_WIDTH = 228;
export const CONTENT_SWITCHER_WIDTH = 400;
export const MODAL_CONTENT_PADDING = 48;

export type FieldKey = keyof Pick<
  PriceSettingsDto,
  | 'localLeadFxRate'
  | 'alternativeFxRate'
  | 'salesDiscount'
  | 'alternativeSalesDiscount'
  | 'functionalDiscount'
  | 'alternativeFunctionalDiscount'
  | 'thresholdBelowMin'
  | 'thresholdBelowMax'
  | 'thresholdPrice'
  | 'thresholdAboveMin'
  | 'thresholdAboveMax'
>;
export type PriceSettingsValues = Record<FieldKey, string>;
export type PriceSettingsPane = 'settings' | 'thresholds';

type PriceSettingsField = {
  label: string;
  key: FieldKey;
  pane: PriceSettingsPane;
  readonly?: boolean;
  decimals?: number;
  isPercentage?: boolean;
};

export const PRICE_SETTINGS_FIELDS: PriceSettingsField[] = [
  {
    label: 'LL IER',
    key: 'localLeadFxRate',
    pane: 'settings',
    readonly: true,
    decimals: 3,
  },
  {
    label: 'Alt. Fx',
    key: 'alternativeFxRate',
    pane: 'settings',
    decimals: 3,
  },
  {
    label: 'Threshold Below Min',
    key: 'thresholdBelowMin',
    pane: 'thresholds',
  },
  {
    label: 'Threshold Below Max',
    key: 'thresholdBelowMax',
    pane: 'thresholds',
  },
  {
    label: 'Threshold Price',
    key: 'thresholdPrice',
    pane: 'thresholds',
  },
  {
    label: 'Threshold Above Min',
    key: 'thresholdAboveMin',
    pane: 'thresholds',
  },
  {
    label: 'Threshold Above Max',
    key: 'thresholdAboveMax',
    pane: 'thresholds',
  },
  {
    label: 'Sales Discount (SD)',
    key: 'salesDiscount',
    pane: 'settings',
    readonly: true,
    decimals: 2,
    isPercentage: true,
  },
  {
    label: 'Alt. Sales Discount (SD)',
    key: 'alternativeSalesDiscount',
    pane: 'settings',
    decimals: 2,
    isPercentage: true,
  },
  {
    label: 'Functional Discount (FD)',
    key: 'functionalDiscount',
    pane: 'settings',
    readonly: true,
    decimals: 2,
    isPercentage: true,
  },
  {
    label: 'Alt. Functional Discount (FD)',
    key: 'alternativeFunctionalDiscount',
    pane: 'settings',
    decimals: 2,
    isPercentage: true,
  },
];

export const getFieldsForPane = (
  pane: PriceSettingsPane,
): Array<{
  label: string;
  key: FieldKey;
  readonly?: boolean;
  isPercentage?: boolean;
}> =>
  PRICE_SETTINGS_FIELDS.filter((field) => field.pane === pane).map(
    ({ label, key, readonly, isPercentage }) => ({
      label,
      key,
      readonly,
      isPercentage,
    }),
  );

export const THRESHOLD_FIELD_KEYS = getFieldsForPane('thresholds').map(
  ({ key }) => key,
);

const isValidDecimal = (value: string, allowZero = true): boolean => {
  const trimmed = value.trim();
  if (trimmed === '') return false;
  const normalized = trimmed.replace(',', '.');
  const num = Number(normalized);
  return Number.isFinite(num) && (allowZero ? num >= 0 : num > 0);
};

const isValidInteger = (value: string): boolean => {
  const trimmed = value.trim();
  if (trimmed === '') return false;
  return /^\d+$/.test(trimmed);
};

export const VALIDATION_RULES: Partial<
  Record<FieldKey, { isValid: (value: string) => boolean; message: string }>
> = {
  alternativeFxRate: {
    isValid: (value) => isValidDecimal(value, false),
    message: 'Must be a non-zero currency rate',
  },
  thresholdBelowMin: {
    isValid: isValidInteger,
    message: 'Must be a whole number',
  },
  thresholdBelowMax: {
    isValid: isValidInteger,
    message: 'Must be a whole number',
  },
  thresholdPrice: {
    isValid: isValidDecimal,
    message: 'Must be a valid number',
  },
  thresholdAboveMin: {
    isValid: isValidInteger,
    message: 'Must be a whole number',
  },
  thresholdAboveMax: {
    isValid: isValidInteger,
    message: 'Must be a whole number',
  },
  alternativeSalesDiscount: {
    isValid: (value) => isValidDecimal(value, true),
    message: 'Must be a valid percentage',
  },
  alternativeFunctionalDiscount: {
    isValid: (value) => isValidDecimal(value, true),
    message: 'Must be a valid percentage',
  },
};

export const toDraftValues = (
  settings?: PricelistSettings,
): PriceSettingsValues =>
  Object.fromEntries(
    PRICE_SETTINGS_FIELDS.map(({ key, decimals, isPercentage }) => [
      key,
      formatDecimal(
        (settings?.[key] ?? 0) * (isPercentage ? 100 : 1),
        decimals ?? 10,
      ),
    ]),
  ) as PriceSettingsValues;

export const parseValue = (value: string, fallback: number | undefined) => {
  const normalized = normalizeNumericString(value);
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : (fallback ?? 0);
};

export const computeOrderedPriceUnits = (
  priceUnits: PriceUnitDto[],
  pricelistSettings: Map<string, PricelistSettings>,
) => {
  const units = priceUnits || [];
  const nameById = new Map<string, string>();
  const priceUnitIds: string[] = [];
  for (const unit of units) {
    const id = String(unit.id);
    nameById.set(id, unit.name ?? id);
    priceUnitIds.push(id);
  }
  const priceUnitIdSet = new Set(priceUnitIds);
  const idsInOrder = [
    ...priceUnitIds.filter((id) => pricelistSettings.has(id)),
    ...Array.from(pricelistSettings.keys()).filter(
      (id) => !priceUnitIdSet.has(id),
    ),
  ];

  return idsInOrder.map((id) => ({
    id,
    label: nameById.get(id) ?? id,
  }));
};

export const DEFAULT_SAME_FOR_ALL: Record<FieldKey, boolean> = {
  localLeadFxRate: false,
  alternativeFxRate: false,
  salesDiscount: false,
  alternativeSalesDiscount: false,
  functionalDiscount: false,
  alternativeFunctionalDiscount: false,
  thresholdBelowMin: true,
  thresholdBelowMax: true,
  thresholdPrice: true,
  thresholdAboveMin: true,
  thresholdAboveMax: true,
};
